/* Trafik — siteye kim, ne zaman, nereden geldi.

   İki bölüm, çünkü iki ayrı sayım var (lib/cfAnalytics.ts, API tarafı):

     Ziyaretçiler   Web Analytics — tarayıcıda ölçülen, botsuz insan
                    ziyareti; kaynak site, cihaz, sayfa hızı.
     Sunucu         Cloudflare'in alan adında saydığı her istek — botlar
                    dahil. Web Analytics kapalıyken tek kaynak bu.

   İkisini tek sayıda birleştirmek yanıltırdı: sunucu tarafının "sayfa
   görüntülemesi" açık arayan botları da sayıyor ve insan ziyaretinin
   birkaç katı çıkıyor. Bu yüzden ayrı başlıklar ve sunucu bölümünde bir
   uyarı cümlesi. */

import { traffic } from '../api.ts';
import type { TrafficReport } from '../api.ts';
import { barChart, dayLabel } from '../chart.ts';
import { el, replace } from '../dom.ts';
import type { AppContext } from '../main.ts';
import { kpi, ranked } from './stats.ts';

type Range = 7 | 30 | 90;

/** Seçili aralık oturum boyunca hatırlanıyor; sekmeler arasında gidip gelince sıfırlanmasın. */
let current: Range = 7;

/**
 * Web Vitals eşikleri (Google'ın "iyi" ve "zayıf" sınırları, p75).
 * Durum her zaman sözcükle de yazılıyor, yalnız renkle değil.
 */
const VITALS = {
  lcp: { good: 2500, poor: 4000, name: 'LCP — ana görselin yüklenmesi', unit: 'ms' },
  inp: { good: 200, poor: 500, name: 'INP — tıklamaya tepki', unit: 'ms' },
  cls: { good: 0.1, poor: 0.25, name: 'CLS — sayfa kayması', unit: '' }
} as const;

export async function renderTraffic(host: HTMLElement, ctx: AppContext): Promise<void> {
  const body = el('div', {}, el('p', { class: 'note', text: 'Cloudflare okunuyor…' }));
  const buttons = ([7, 30, 90] as const).map((range) =>
    el('button', {
      type: 'button',
      text: `${range} gün`,
      'aria-pressed': String(range === current),
      onclick: () => {
        current = range;
        void renderTraffic(host, ctx);
      }
    })
  );

  replace(
    host,
    el('div', { class: 'row' }, el('h1', { text: 'Trafik', style: 'margin:0' }), el('span', { class: 'spacer' }), ...buttons),
    el('div', { style: 'height:14px' }),
    body
  );

  try {
    const report = await traffic(current);
    replace(body, ...draw(report));
  } catch (err) {
    replace(body, el('p', { class: 'note', text: err instanceof Error ? err.message : 'Trafik verisi okunamadı.' }));
  }
}

function draw(r: TrafficReport): HTMLElement[] {
  const n = (x: number) => x.toLocaleString('tr-TR');
  const sum = (pick: (d: TrafficReport['days'][number]) => number) => r.days.reduce((s, d) => s + pick(d), 0);
  const shortRange = r.days.length < r.range;

  const out: HTMLElement[] = [];

  /* ── Ziyaretçiler ── */
  out.push(el('h2', { text: 'Ziyaretçiler' }));
  if (!r.rum) {
    out.push(
      el(
        'div',
        { class: 'banner', 'data-kind': 'bad', style: 'color:var(--ink)' },
        el('p', { text: 'Web Analytics henüz veri göndermiyor.' }),
        el('p', {
          class: 'note',
          text:
            'Açmak için: Cloudflare → Workers & Pages → frawgraphy → Metrics → Web Analytics → Enable. ' +
            'Site CSP\'si betiğe izin verecek şekilde güncellendi; açıldıktan sonraki ilk yayınla insan ziyaretleri, ' +
            'kaynak siteler, cihazlar ve sayfa hızı burada görünür.'
        })
      )
    );
  } else {
    const visits = sum((d) => d.visits);
    const totalRum = r.rumCountries.reduce((s, c) => s + c.count, 0);
    out.push(
      el(
        'div',
        { class: 'tiles' },
        kpi('Ziyaret', n(visits), 'botlar hariç'),
        kpi('Sayfa yükleme', n(sum((d) => d.pageloads)), `ziyaret başına ${(sum((d) => d.pageloads) / Math.max(1, visits)).toFixed(1)}`),
        ...vitalTiles(r.vitals)
      ),
      el('div', { style: 'height:16px' }),
      barChart({
        label: `Günlük ziyaret, son ${r.range} gün`,
        bars: r.days.map((d) => ({ at: Date.parse(d.date), value: d.visits, detail: [`${n(d.visits)} ziyaret`, `${n(d.pageloads)} sayfa yükleme`] })),
        unit: 'ziyaret',
        format: dayLabel,
        height: 170
      }),
      el(
        'div',
        { class: 'cols' },
        ranked('Sayfalar', r.rumPaths, r.rumPaths.reduce((s, p) => s + p.count, 0)),
        ranked('Nereden geldi', r.referrers, visits),
        ranked('Ülke', r.rumCountries, totalRum),
        ranked('Cihaz', r.devices, visits)
      )
    );
  }

  /* ── Sunucu ── */
  const requests = sum((d) => d.requests);
  out.push(
    el('h2', { text: 'Sunucu tarafı — botlar dahil' }),
    el('p', {
      class: 'note',
      text:
        'Cloudflare\'in alan adında saydığı her istek. Arama motorları, önizleme botları ve açık tarayan ' +
        'otomatik araçlar da burada — insan ziyaretinin birkaç katı çıkması normal.'
    }),
    el(
      'div',
      { class: 'tiles' },
      kpi('Sayfa görüntüleme', n(sum((d) => d.pageViews)), 'HTML istekleri'),
      kpi('Tekil ziyaretçi', n(sum((d) => d.uniques)), 'günlük tekillerin toplamı'),
      kpi('Aktarılan veri', mb(sum((d) => d.bytes)), `önbellekten %${requests > 0 ? Math.round((sum((d) => d.cached) / requests) * 100) : 0}`),
      kpi('Tehdit', n(sum((d) => d.threats)), 'Cloudflare\'in durdurduğu')
    ),
    el('div', { style: 'height:16px' }),
    barChart({
      label: `Günlük sayfa görüntüleme (sunucu), son ${r.range} gün`,
      bars: r.days.map((d) => ({
        at: Date.parse(d.date),
        value: d.pageViews,
        detail: [`${n(d.pageViews)} sayfa görüntüleme`, `${n(d.uniques)} tekil`, `${n(d.requests)} istek`]
      })),
      unit: 'görüntüleme',
      format: dayLabel,
      height: 170
    }),
    el(
      'div',
      { class: 'cols' },
      ranked(`En çok istenen sayfalar (son ${Math.min(r.range, 30)} gün)`, r.pages, r.pages.reduce((s, p) => s + p.count, 0)),
      ranked('Ülke (istek)', r.countries, requests)
    ),
    el('p', {
      class: 'dim',
      text:
        (shortRange ? `Cloudflare bu alan adı için ${r.days.length} günlük veri tutuyor. ` : '') +
        `Veri ${new Date(r.cachedAt).toLocaleTimeString('tr-TR')} itibarıyla; beş dakikada bir tazelenir.`
    })
  );
  return out;
}

function vitalTiles(v: TrafficReport['vitals']): HTMLElement[] {
  if (!v) return [kpi('Sayfa hızı', '—', 'henüz ölçüm yok')];
  return (Object.keys(VITALS) as (keyof typeof VITALS)[]).map((key) => {
    const spec = VITALS[key];
    const value = v[key];
    const verdict = value <= spec.good ? ['ok', '● iyi'] : value <= spec.poor ? ['dim', '◐ geliştirilmeli'] : ['bad', '✕ zayıf'];
    const shown = key === 'cls' ? value.toFixed(2) : `${n0(value)} ${spec.unit}`;
    return el(
      'div',
      { class: 'tile' },
      el('p', { class: 'tile-name', text: spec.name }),
      el('p', { class: 'tile-value', text: shown }),
      el('p', { class: verdict[0]!, text: `${verdict[1]} · p75, ${v.samples} ölçüm` })
    );
  });
}

function n0(x: number): string {
  return Math.round(x).toLocaleString('tr-TR');
}

function mb(bytes: number): string {
  return bytes >= 1e9 ? `${(bytes / 1e9).toFixed(2)} GB` : `${(bytes / 1e6).toFixed(1)} MB`;
}
